/**
 * Shared constants and pure helpers used across the Dashboard module.
 * Extracted from DashboardView.jsx to reduce its size and keep pure
 * logic unit-testable.
 */

export const CATEGORY_ICONS = {
  motos: 'Bike', vehiculos: 'Car', ferreteria: 'Hammer',
  educacion: 'GraduationCap', navidades: 'Gift', salud: 'HeartPulse',
  tecnologia: 'Monitor', viajes: 'Plane',
};

export const MOCK_KOMPAS = [
  { nombre: 'Maria Lopez', aportado: 420 },
  { nombre: 'Carlos Perez', aportado: 310 },
  { nombre: 'Ana Rodriguez', aportado: 280 },
  { nombre: 'Pedro Gomez', aportado: 195 },
];

export const MOCK_GROUP_MEMBERS = [
  { id: 'm1', nombre: 'Maria Lopez', color: '#fbb040', hasPaid: true, aportado: 420 },
  { id: 'm2', nombre: 'Carlos Perez', color: '#1f2d5a', hasPaid: true, aportado: 310 },
  { id: 'm3', nombre: 'Ana Rodriguez', color: '#4CAF50', hasPaid: false, aportado: 280 },
  { id: 'm4', nombre: 'Pedro Gomez', color: '#E91E63', hasPaid: true, aportado: 195 },
  { id: 'm5', nombre: 'Laura Martinez', color: '#9C27B0', hasPaid: false, aportado: 150 },
];

export const isGroupGoal = (goal) =>
  goal?.modality === 'club' || goal?.subModality === 'grupal';

/**
 * Stricter predicate used to gate UI surfaces that ONLY make sense for
 * small closed groups ("Ahorra entre tus kompas" — modality !== 'club'
 * with subModality === 'grupal'). Excludes:
 *   - "Ahorro Total Individual"  → subModality === 'individual'
 *   - "Ahorro en Comunidad"      → modality === 'club' (global pool)
 */
export const isSmallClosedGroup = (goal) =>
  goal?.modality !== 'club' && goal?.subModality === 'grupal';

const MONTH_MAP = {
  ene: 0, feb: 1, mar: 2, abr: 3, may: 4, jun: 5,
  jul: 6, ago: 7, sep: 8, oct: 9, nov: 10, dic: 11,
};

export const parseGoalDate = (dateStr) => {
  if (!dateStr) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  }
  const parts = dateStr.trim().split(/\s+/);
  if (parts.length < 2) return null;
  const day = parseInt(parts[0], 10);
  const monthKey = parts[1].toLowerCase().replace('.', '').slice(0, 3);
  const month = MONTH_MAP[monthKey];
  if (month === undefined || isNaN(day)) return null;
  const year = parts[2] ? parseInt(parts[2], 10) : new Date().getFullYear();
  return new Date(year, month, day);
};

const SIXTY_DAYS_MS = 60 * 24 * 60 * 60 * 1000;

export const checkGoalInactive = (goal) => {
  if (!goal) return false;
  if (goal.status && goal.status !== 'active') return false;
  const now = Date.now();
  if (Array.isArray(goal.payments) && goal.payments.length > 0) {
    let latest = null;
    for (const p of goal.payments) {
      const d = parseGoalDate(p?.date);
      if (d && (!latest || d > latest)) latest = d;
    }
    if (latest) return now - latest.getTime() > SIXTY_DAYS_MS;
  }
  const start = parseGoalDate(goal.startDate);
  if (start) return now - start.getTime() > SIXTY_DAYS_MS;
  return false;
};

export const computeExpectedGross = (goal, templates) => {
  if (!goal) return 0;
  const tpl = Array.isArray(templates) ? templates.find(t => t?.code === goal.planCode) : null;
  const feePct = tpl?.serviceFeePct || 0;
  return (Number(goal.totalAmount) || 0) * (1 + feePct);
};

export const getProgressGradient = (pct) => {
  if (pct < 25) return 'from-red-500 to-orange-400';
  if (pct < 50) return 'from-orange-400 to-yellow-400';
  if (pct < 75) return 'from-yellow-400 to-lime-400';
  return 'from-lime-400 to-green-500';
};
